import { useEffect, useState } from "react";
import { app } from "js/namespaces";

export type DeviceType = "mobile" | "tablet" | "desktop";

interface UseScreenSizeReturn {
    deviceType: DeviceType;
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    width: number;
    height: number;
}

interface UseScreenSizeOptions {
    static?: boolean;
}

const MOBILE_BREAKPOINT = 652;
const TABLET_BREAKPOINT = 768;

const getDeviceType = (width: number): DeviceType => {
    if (width < MOBILE_BREAKPOINT) return "mobile";
    if (width < TABLET_BREAKPOINT) return "tablet";
    if (app.isMobileOrTablet) return "mobile";
    return "desktop";
};

export const useScreenSize = (options?: UseScreenSizeOptions): UseScreenSizeReturn => {
    const [windowSize, setWindowSize] = useState({
        width: typeof window !== "undefined" ? window.innerWidth : 0,
        height: typeof window !== "undefined" ? window.innerHeight : 0,
    });

    useEffect(() => {
        // Skip resize listener if static mode is enabled
        if (options?.static) return;

        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [options?.static]);

    const deviceType = getDeviceType(windowSize.width);

    return {
        deviceType,
        isMobile: deviceType === "mobile",
        isTablet: deviceType === "tablet",
        isDesktop: deviceType === "desktop",
        width: windowSize.width,
        height: windowSize.height,
    };
};
